import styled, { css, keyframes } from 'styled-components';
import { transparentize } from 'polished';
import { DIMENSIONS } from '@hqo/react-components-library/dist/utils';
import IframeModalWrapper from 'components/modals/iframeWrapper';
import { Button } from '@hqo/react-components-library/dist/atoms/button';

import {
  NATIVE_FLOORPLAN_DIMMED_OPACITY,
  NATIVE_FLOORPLAN_FAVORITE_RING,
  NATIVE_FLOORPLAN_PLACEMENT_FILL_ALPHA,
  NATIVE_FLOORPLAN_FOCUS_MODE,
  NATIVE_FLOORPLAN_MAP_BACKGROUND,
  NATIVE_FLOORPLAN_SELECTED_COLOR,
  PLACEMENT_STATUS_COLORS,
} from '../const';
import { PlacementStatus } from '../utils/placement-status';

/**
 * Breathing room between the plan and the edges of its viewport. In the design the plan sits as a
 * smaller card in the middle of the grey ground — meant to be read whole first and zoomed into for
 * detail, not to fill the screen edge to edge.
 */
const VIEWPORT_INSET = '24px 10%';

/**
 * The area the plan sits in. Takes the space the controls above it leave over — `flex: 1` with
 * `min-height: 0`, not `height: 100%`, because a flex item refuses to shrink below its content
 * without it and the map would push the page instead of fitting in it.
 *
 * `position: relative` is the anchor the overlaid controls (zoom, the selected-desk popup) will use.
 * The prototype also sets `touch-action: none` — that belongs with the pinch/pan gestures, and
 * setting it before they exist would only kill ordinary scrolling.
 */
export const CanvasViewport = styled.div<{ isPannable?: boolean }>`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Inset rather than a full-bleed plan: the drawing reads as a card on the grey ground, which is
     also what leaves somewhere to pan into once zoomed. The zoom buttons are positioned against
     the viewport itself, so they stay in its corner regardless. */
  padding: ${VIEWPORT_INSET};
  background: ${NATIVE_FLOORPLAN_MAP_BACKGROUND};
  border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  cursor: ${({ isPannable }) => (isPannable ? 'grab' : 'default')};
  /* Only take the gestures away from the browser once there is something to drag. Until then
     "pan-y" keeps the page scrolling under a finger, while still denying the browser its own
     pinch-zoom so the map can handle the pinch that zooms it in. */
  touch-action: ${({ isPannable }) => (isPannable ? 'none' : 'pan-y')};
`;

/* Carries the zoom/pan transform. Separate from the viewport so the zoom buttons, which are pinned
   to the viewport, stay put while the plan moves under them. */
export const TransformLayer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transform-origin: center center;
`;

export const ZoomControls = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 1px 2px rgba(23, 30, 38, 0.08);
`;

export const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.palette.systemBlack};
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.colors.palette.systemGreyMedium};
    cursor: default;
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  }
`;

/**
 * Wraps the image and nothing else, so it is exactly the size of the rendered drawing — which is
 * what lets the overlay position rects in percentages with no measuring, no resize listener and
 * nothing to recompute on rotation.
 *
 * The frame deliberately does NOT carry the aspect ratio itself. Sizing a container by
 * `aspect-ratio` plus `max-width`/`max-height` breaks down when the second constraint bites: the
 * box keeps one axis and clips the other, the ratio no longer matches the image, and the drawing
 * stretches. Letting the replaced element size itself is the browser's own job and cannot disagree
 * with the image it just laid out.
 */
export const PlanFrame = styled.div`
  position: relative;
  display: flex;
  max-width: 100%;
  max-height: 100%;
`;

export const PlanImage = styled.img`
  display: block;
  /* Fits whichever axis runs out first while keeping its own proportions. */
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
`;

export const PlacementLayer = styled.div`
  position: absolute;
  inset: 0;
`;

export const PlacementRect = styled.button<PlacementRectProps>`
  position: absolute;
  padding: 0;
  border-radius: 4px;
  cursor: ${({ isDimmed }) => (isDimmed ? 'default' : 'pointer')};
  opacity: ${({ isDimmed, isFocusMode, status }) => {
    if (isDimmed) {
      return NATIVE_FLOORPLAN_DIMMED_OPACITY;
    }

    // Focus mode steps free desks back so the group's members carry the map; mine stays as is.
    return isFocusMode && status === 'available' ? NATIVE_FLOORPLAN_FOCUS_MODE.availableOpacity : 1;
  }};
  /* A dimmed placement carries the disabled attribute too, but that alone would swallow the drag:
     the browser dispatches no mouse events on a disabled control, so a pan started on top of one
     would go nowhere. Taking it out of hit-testing lets the gesture reach the viewport underneath. */
  pointer-events: ${({ isDimmed }) => (isDimmed ? 'none' : 'auto')};
  transition: opacity 150ms;
  background: ${({ status }) => `${PLACEMENT_STATUS_COLORS[status].fill}${NATIVE_FLOORPLAN_PLACEMENT_FILL_ALPHA}`};
  border: ${({ status, isSelected, isFocused }) => {
    if (isFocused) {
      return `3.5px solid ${NATIVE_FLOORPLAN_SELECTED_COLOR}`;
    }

    return isSelected
      ? `2.5px solid ${NATIVE_FLOORPLAN_SELECTED_COLOR}`
      : `1.5px solid ${PLACEMENT_STATUS_COLORS[status].stroke}`;
  }};
  box-shadow: ${({ isSelected, isFocused, isFavoriteHighlighted }) => {
    if (isFocused) {
      return `0 0 0 4px ${NATIVE_FLOORPLAN_SELECTED_COLOR}4d`;
    }

    if (isSelected) {
      return `0 0 0 2px ${NATIVE_FLOORPLAN_SELECTED_COLOR}66`;
    }

    // The prototype's `showFavRing`: system-gold, 3px, on a favorite the other filters leave lit.
    if (isFavoriteHighlighted) {
      return `0 0 0 3px ${NATIVE_FLOORPLAN_FAVORITE_RING}`;
    }

    return 'none';
  }};

  &:focus-visible {
    outline: 2px solid ${NATIVE_FLOORPLAN_SELECTED_COLOR};
    outline-offset: 2px;
  }
`;

export const FloorChipsRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  flex-shrink: 0;
  /* No top margin: the gap above comes from FilterSection's bottom margin, so adding one here
     would make this row sit further from the filters than the filters sit from the toggle.
     The bottom margin is that same 20px, which is both the page's rhythm (toggle row and filter
     section use it) and what the design leaves under the chips — and the grey map ground below
     adds its own inset on top of it, so anything wider reads as a hole in the page. */
  margin: 0 0 20px;
  padding-bottom: 2px;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    margin-bottom: 10px;
  }
  /* Chips are a horizontal scroller in the design; hide the bar but keep the gesture. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FloorChip = styled.button<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 9999px;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  /* The accent is the building's brand color, not the prototype's literal orange — this app is
     white-labelled per building. */
  border: 1px solid
    ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.palette.systemGreyMedium)};
  background: ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.universal.white)};
  color: ${({ isActive, theme }) => (isActive ? theme.colors.universal.white : theme.colors.palette.systemBlack)};
`;

export const ViewToggle = styled.div`
  /* Prototype: pill track holding both options, no gap — the active pill's white fill is the
     only separation. */
  display: flex;
  border-radius: 9999px;
  padding: 3px;
  background: ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const ViewToggleButton = styled.button<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 13px;
  border: none;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ isActive, theme }) => (isActive ? theme.colors.universal.white : 'transparent')};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.palette.systemBlack : theme.colors.palette.systemGreyDark};
  box-shadow: ${({ isActive }) => (isActive ? '0 1px 2px rgba(23, 30, 38, 0.08)' : 'none')};
`;

/* Row holding the toggle and, on mobile, the "My bookings" link — the prototype puts them on one
   line with the toggle on the left. */
export const ViewToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  /* Holds the toggle's height from the first frame, so "My bookings" does not shift down when the
     toggle arrives with the floorplan list. */
  min-height: 36px;
  /* Bottom gap matches FilterSection's, so the toggle, the location/date row and the floor chips
     are spaced on one rhythm. */
  margin: 0 0 20px;

  /* "My bookings" carries a bottom margin of its own for when it stands alone above the list.
     Inside this row that margin makes its box taller than the toggle, so centring lifts its
     content off the toggle's centre line. */
  & > * {
    margin-bottom: 0;
  }
`;

/* Roughly the footprint a plan takes in the viewport, so the skeleton reads as "the map goes here"
   rather than as an arbitrary block. */
export const SkeletonFrame = styled.div`
  width: 92%;
  height: 92%;
  max-width: 100%;
  max-height: 100%;
`;

// What the resources list already subtracts for the header and filters above it. `ViewToggleRow`
// is already inside that number: `resources.tsx` renders it on every mobile screen (`isMobileDevice
// ||…`) and on desktop whenever the toggle is available at all — the same condition that has to be
// true for this floorplan view to be reachable in the first place. So the list and the floorplan
// header stack are identical up to this point, and only the floor chips are genuinely extra.
// Adding a `VIEW_TOGGLE_ROW_HEIGHT` on top of this used to double-count that row, pushing the map's
// top edge (and the blank gap above it) down by the row's own height on every screen size.
const LIST_TOP_OFFSET = 290;
const LIST_TOP_OFFSET_MOBILE = 208;
// Plus the one row only this view adds, including the margin under it.
const FLOOR_CHIPS_ROW_HEIGHT = 70;
const FLOOR_CHIPS_ROW_HEIGHT_MOBILE = 48;

const MAP_TOP_OFFSET = LIST_TOP_OFFSET + FLOOR_CHIPS_ROW_HEIGHT;
const MAP_TOP_OFFSET_MOBILE = LIST_TOP_OFFSET_MOBILE + FLOOR_CHIPS_ROW_HEIGHT_MOBILE;

/**
 * RES-1226: this block used to size itself with `min-height: calc(100vh - offset)`, trusting that
 * `100vh` reaches the same bottom edge the eye sees. In the miniapp webview that isn't true, so the
 * map+legend fell short of the real screen bottom — leaving a stretch of blank page background
 * below the colour key, and making the selected-resource sheet (position: fixed, anchored to the
 * *true* viewport bottom — see PlacementDetailsModal below) read as floating too high above it,
 * since it was always correctly placed relative to an edge the key itself wasn't reaching.
 *
 * `position: fixed` with `bottom: 0` sidesteps the vh guesswork entirely for the bottom edge: it is
 * always the real edge of the viewport no matter what "100vh" resolves to here. `top` keeps the same
 * MAP_TOP_OFFSET(_MOBILE) the old min-height calc used — that edge was never the reported problem
 * (everything above this point is still normal document flow, so it already starts exactly where
 * the controls above it end) and reusing the value keeps it exactly as accurate as it already was.
 * (If the controls above ever grow past that estimate on some viewport, this fixed top will start
 * overlapping them instead of being pushed down the way flow would have handled it — the same risk
 * the old calc already carried for the bottom edge, just moved to the top.)
 *
 * Fixed positioning escapes the page column's own padding, so the horizontal inset can't be
 * inherited any more — it's replicated here from pages/resources/styles.ts's `Container`, the one
 * place that padding is meant to live. Keep the two in sync if that ever changes.
 */
// pages/resources/styles.ts Container's own left/right padding at each breakpoint.
const CONTAINER_PADDING_DESKTOP = 52;
const CONTAINER_PADDING_MEDIUM = 16;
const CONTAINER_PADDING_SMALL = 0;

export const FloorplanFixedColumn = styled.div`
  position: fixed;
  top: ${MAP_TOP_OFFSET}px;
  bottom: 0;
  left: ${CONTAINER_PADDING_DESKTOP}px;
  right: ${CONTAINER_PADDING_DESKTOP}px;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    top: ${MAP_TOP_OFFSET_MOBILE}px;
    left: ${CONTAINER_PADDING_MEDIUM}px;
    right: ${CONTAINER_PADDING_MEDIUM}px;
  }

  @media only screen and (max-width: ${DIMENSIONS.SMALL}) {
    left: ${CONTAINER_PADDING_SMALL}px;
    right: ${CONTAINER_PADDING_SMALL}px;
  }
`;

/**
 * The plan's own area within the fixed column above. `flex: 1` with `min-height: 0` (not
 * `height: 100%`) so it takes exactly the space `LegendRow` below it doesn't need — the same
 * pattern `CanvasViewport` already uses one level down, except this time the flex column really
 * does have a definite height to share, since `FloorplanFixedColumn` is anchored top-to-bottom
 * rather than relying on an ancestor's `height: 100%` that never resolves (see its own comment).
 */
export const FloorplanViewContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

/* Colour key pinned under the map, matching the design's bottom bar. */
export const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  gap: 8px 16px;
  padding: 11px 0;
  background: ${({ theme }) => theme.colors.universal.white};
  border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};

  /* The legend sits directly in the page column, so it only gets that column's padding — the
     controls above it pick up another 13px from HeaderContentContainer. Matching it here keeps the
     legend lined up with the filters and floor chips instead of running to the screen edge. */
  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    padding: 11px 13px;
  }
`;

export const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const LegendDot = styled.span<{ fill: string; stroke: string }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ fill }) => fill};
  border: 1.5px solid ${({ stroke }) => stroke};
`;

/** Gap between the card and the screen edges, as in the design. */
const SHEET_INSET = 16;
/**
 * How far the card sits above the bottom edge: just clear of the colour legend, which stays
 * readable while a desk is being considered.
 *
 * This used to be a guess at the legend's height (11px padding + ~15px content + 1px border) baked
 * in as a constant. That only held for a legend that renders on one line — `LegendRow` wraps
 * (`flex-wrap: wrap`), and a longer translation, a coworker-mode item, or a larger system text size
 * (all more likely to bite on iOS, where this first surfaced) can push it onto two, at which point a
 * fixed guess is either floating the card over blank canvas or too high above it entirely — "little
 * bit higher than it should be" is exactly what an under-measured two-line legend looks like.
 *
 * `FloorplanLegend` publishes the row's *actual* rendered height to `--rb-floorplan-legend-height`
 * on `<html>` (a CSS var, because this card is portaled straight to `document.body` by
 * `IframeModalWrapper` and so shares no DOM ancestor with the legend to size itself against
 * directly). This constant now only backstops the instant before that effect runs.
 */
const SHEET_LEGEND_HEIGHT_FALLBACK = 42;
/** Clearance kept above the legend's real height, once known. */
const SHEET_LEGEND_CLEARANCE = 8;

/**
 * Details sheet for the tapped placement — the same modal quick checkout uses.
 *
 * Not a SwipeModal: that one derives its height from a slide position (`screenHeight -
 * initialPosition`), so a sheet whose size is simply its content would have to measure itself and
 * feed a number back in, and any CSS override fights the inline position it sets while dragging.
 * Here the height is plain CSS, and there is no drag gesture to compete with the map's own.
 */
export const PlacementDetailsModal = styled(IframeModalWrapper)`
  /* No dimmed backdrop: the card is a detail of the plan behind it, and the plan has to stay
     readable while the user decides — dimming it would hide exactly what is being chosen from. */
  .iframe-background {
    display: none;
  }

  /* A card floating over the plan rather than a full-width sheet: the design keeps the map and the
     legend visible around it, so the user can see what they are choosing between. */
  .iframe-wrapper {
    top: auto;
    bottom: calc(var(--rb-floorplan-legend-height, ${SHEET_LEGEND_HEIGHT_FALLBACK}px) + ${SHEET_LEGEND_CLEARANCE}px);
    left: ${SHEET_INSET}px;
    right: ${SHEET_INSET}px;
    width: auto;
    height: auto;
    margin: 0;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(23, 30, 38, 0.16);
  }

  .iframe-modal-content {
    height: auto;
    max-height: 60vh;
    border-radius: 16px;

    & > div {
      height: auto;
    }
  }

  @media only screen and (min-width: ${DIMENSIONS.MEDIUM}) {
    .iframe-wrapper {
      top: 50%;
      bottom: auto;
      left: 50%;
      right: auto;
      width: 420px;
      max-width: calc(100% - ${SHEET_INSET * 2}px);
      transform: translate(-50%, -50%);
    }
  }
`;
export const PlacementDetailsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;
/* The header's trailing controls: the favorite star beside Close, kept as one flex item so the
   header's space-between still pushes them to the right edge together. */
export const PlacementDetailsActions = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 4px;
`;
export const PlacementDetailsClose = styled.button<{ isCompact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ isCompact }) => (isCompact ? '24px' : '32px')};
  height: ${({ isCompact }) => (isCompact ? '24px' : '32px')};
  margin: ${({ isCompact }) => (isCompact ? '-2px -4px 0 0' : '-4px -8px 0 0')};
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;
/*
 * The card's contents at two sizes. The sheet on mobile is the page's only surface at that moment,
 * so it is sized like the app's other sheets; the web popover hangs off a desk on a plan the user is
 * reading around it, so it takes the prototype's popover metrics instead — its own padding comes from
 * `PlacementPopoverCard`, which is why compact adds none of its own.
 */
export const PlacementDetailsBody = styled.div<{ isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isCompact }) => (isCompact ? '8px' : '12px')};
  /* 16px is what the other sheets in the app use. */
  padding: ${({ isCompact }) => (isCompact ? '0' : '20px 16px 24px')};
`;
export const PlacementTitle = styled.div<{ isCompact?: boolean }>`
  font: ${({ isCompact }) => (isCompact ? '700 15px/20px' : '700 20px/26px')} ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
`;
export const PlacementStatusRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
`;
export const PlacementStatusLabel = styled.span<{ color: string; isCompact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: ${({ isCompact }) => (isCompact ? '600 12px/16px' : '600 14px/20px')} ${({ theme }) => theme.fonts.join()};
  color: ${({ color }) => color};
`;
export const PlacementTemperature = styled.div<{ isCompact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 7px;
  font: ${({ isCompact }) => (isCompact ? '400 12px/16px' : '400 14px/20px')} ${({ theme }) => theme.fonts.join()};
  /* Plain black per the BUILD-1177 decision — a live reading, not secondary
     metadata, and never a band color. */
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const PlacementSections = styled.span<{ isCompact?: boolean }>`
  font: ${({ isCompact }) => (isCompact ? '400 12px/16px' : '400 14px/20px')} ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;
/**
 * The library's `Primary` variant paints itself from the admin palette (`colors.admin.blue`), which
 * ignores the building's branding — next to the floor chips, which do follow it, the button read as
 * a different product. Repainted from the theme so every accent on this screen matches.
 */
export const PlacementDetailsAction = styled(Button)<{ isCompact?: boolean }>`
  width: 100%;
  ${({ isCompact }) => isCompact && 'height: 36px; min-height: 36px; font-size: 13px;'}
  background-color: ${({ theme }) => theme.colors.primary};
  border-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.universal.white};

  transition: opacity 120ms;

  /*
   * The :not([disabled]) part is not decoration — the library's variants hover with exactly that
   * selector, so a bare &:hover loses on specificity and their blue came through over the brand
   * colour.
   *
   * The state reads as a dip in opacity rather than a second colour, which is what the library's own
   * ghost variants do, and it keeps a white-labelled button branded while it is being pressed.
   */
  &:hover:not([disabled]),
  &:focus:not([disabled]),
  &:active:not([disabled]) {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    opacity: 0.9;
  }
`;

interface PlacementRectProps {
  status: PlacementStatus;
  isSelected: boolean;
  /** A coworker jump's target: a thicker ring than selection, and not a selected state (§C2). */
  isFocused: boolean;
  isDimmed: boolean;
  /** Favorites highlight: a favorite that is still lit wears the gold ring. */
  isFavoriteHighlighted: boolean;
  /** An active coworker group (§C3): members ringed, other occupied desks grey, free desks stepped back. */
  isFocusMode: boolean;
}

/**
 * The map's filter control: the prototype's outlined square button, sized to the filter row rather
 * than to the prototype's 44px — it stands next to the search field, which is 36px tall here.
 *
 * Active state follows the prototype's accent handling, with the building's brand color standing in
 * for its literal blue (this app is white-labelled per building).
 */
export const FilterButton = styled.button<{ isActive: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  cursor: pointer;
  border: 1.5px solid
    ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.palette.systemGreyMedium)};
  /* The prototype tints the active button with its accent at 8% over white (a color-mix against
     #fff); the library's ghost tokens are fixed blues, so the tint is mixed from the brand primary
     instead of picking one that would clash with the border above. */
  background: ${({ isActive, theme }) =>
    isActive ? transparentize(0.92, theme.colors.primary) : theme.colors.universal.white};
  color: ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.palette.systemBlack)};
`;

/* Count of applied filters, hanging off the button's corner as in the prototype. The white ring is
   what keeps it legible where it overlaps the button's own border. */
export const FilterCountBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9999px;
  border: 2px solid ${({ theme }) => theme.colors.universal.white};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.universal.white};
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
`;

/*
 * The Filters surface. Neither the shared `Modal` nor the library's `SwipeModal` fits the design
 * here: the first hangs a min-height-348 box 24px from the top of the page (which is why this sheet
 * sat above centre), and the second is a full-height sheet by construction (`100vh - 48px`), which
 * left most of the phone empty under two chips. Both shells are built to the prototype instead —
 * a centred 440px panel on the web, a bottom sheet that hugs its content on mobile.
 */
export const FiltersOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  background: ${({ theme }) => transparentize(0.5, theme.colors.palette.systemBlackAlt)};
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

/* Bottom sheet: as tall as its content, capped so a long section list still leaves the map visible
   above it (the prototype's `max-height:82%`). */
export const FiltersSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 21;
  display: flex;
  flex-direction: column;
  max-height: 82%;
  padding: 8px 22px 24px;
  box-sizing: border-box;
  border-radius: 24px 24px 0 0;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 -12px 30px -12px rgba(11, 14, 18, 0.4);
  animation: ${slideUp} 0.3s ease-out;
`;

/* The grab bar above the title. Decoration, not a control: the sheet is dismissed by its close
   button or by the ground behind it. */
export const FiltersSheetHandle = styled.div`
  width: 40px;
  height: 5px;
  flex-shrink: 0;
  margin: 6px auto 14px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.palette.systemGreyMedium};
`;

/* Web panel: centred on the viewport, which is where a filters dialog belongs and what the shared
   modal's top margin prevented. */
export const FiltersPanel = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 21;
  display: flex;
  flex-direction: column;
  width: 440px;
  max-width: calc(100% - 32px);
  max-height: min(600px, calc(100% - 48px));
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 24px 48px -12px rgba(11, 14, 18, 0.25);
`;

const filtersRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`;

export const FiltersSheetHeader = styled.div`
  ${filtersRow}
  margin-bottom: 18px;
`;

export const FiltersPanelHeader = styled.div`
  ${filtersRow}
  padding: 18px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const FiltersSheetTitle = styled.div`
  font: 700 19px/24px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const FiltersPanelTitle = styled.div`
  font: 600 16px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

/* Round grey close button from the prototype, in place of the shared modals' bare cross. */
export const FiltersCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.palette.systemGreyLight};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
`;

/* Only the filter groups scroll; the header and the buttons stay put, which is what makes a long
   section list usable on a phone. */
export const FiltersBody = styled.div<{ hasPanelPadding?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  ${({ hasPanelPadding }) => hasPanelPadding && 'padding: 18px 20px 20px;'}
`;

/* Mobile: two buttons side by side, the CTA the wider of them (the prototype's 1 : 1.4). */
export const FiltersSheetFooter = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  margin-top: 26px;

  & > *:first-child {
    flex: 1;
  }

  & > *:last-child {
    flex: 1.4;
  }
`;

/* Web: "Clear all" is a text link beside the CTA, not a second button. */
export const FiltersPanelFooter = styled.div`
  ${filtersRow}
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const FiltersClearLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: 600 13px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme, disabled }) => (disabled ? theme.colors.palette.systemGreyMediumAlt : theme.colors.primary)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

/*
 * The sheet's own buttons rather than the library `Button`: its variants bring their own radius and
 * their own fixed palette, which is what made these two differently rounded and left the CTA the
 * library's navy instead of the building's brand color.
 *
 * Prototype geometry — both 50px tall with an 11px radius, the same 15px/600 label — so the pair
 * reads as one control split in two.
 */
const filtersSheetButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 16px;
  border-radius: 11px;
  box-sizing: border-box;
  font: 600 15px/20px ${({ theme }) => theme.fonts.join()};
  white-space: nowrap;
  transition: background-color 120ms, color 120ms;
`;

export const FiltersSheetClearButton = styled.button`
  ${filtersSheetButton}
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  background: ${({ theme }) => theme.colors.universal.white};

  /* White on white has no opacity to give, so this one hovers the way the prototype's outline button
     does — a grey tint. */
  &:hover:not([disabled]) {
    background: ${({ theme }) => theme.colors.palette.systemGreyLight};
  }
  border: 1px solid
    ${({ theme, disabled }) =>
      disabled ? theme.colors.palette.systemGreyLight : theme.colors.palette.systemGreyMedium};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.palette.systemGreyMediumAlt : theme.colors.palette.systemBlack};
`;

export const FiltersSheetCtaButton = styled.button`
  ${filtersSheetButton}
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.universal.white};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  /* Same dip in opacity the details action uses, so the map's brand-filled buttons all answer the
     pointer the same way. */
  &:hover:not([disabled]) {
    opacity: 0.9;
  }
`;

/* Web panel CTA: the prototype's `.btn.btn-primary` — shorter and less rounded than the sheet's,
   because it sits in a dialog footer rather than under the thumb. */
export const FiltersPanelCtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  box-sizing: border-box;
  font: 500 14px/18px ${({ theme }) => theme.fonts.join()};
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.universal.white};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: opacity 120ms;

  &:hover:not([disabled]) {
    opacity: 0.9;
  }
`;

export const FilterGroupTitle = styled.div`
  margin-bottom: 10px;
  font: 700 14px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

/* Chips wrap here instead of scrolling: the sheet has the width, and a hidden scroller inside a
   modal is a filter the user never finds. */
export const FilterChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/* The section's own color, so a chip reads as the same section the admin coloured on the plan. */
export const SectionDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ color }) => color};
`;

/*
 * The details card hanging off the placement it belongs to, on the web.
 *
 * It lives inside the transformed plan, so it stays glued to its placement through a pan or a zoom
 * with no measuring and no per-frame work. The card counter-scales, which is what keeps its text the
 * same size at every zoom level — see `PlacementPopoverCard`.
 */
export const PlacementPopoverAnchor = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  /* Above the placements, so a neighbouring rect cannot overlap the card. */
  z-index: 2;
`;

const popoverTranslateX = { start: '0', center: '-50%', end: '-100%' } as const;

interface PlacementPopoverCardProps {
  scale: number;
  alignX: 'start' | 'center' | 'end';
  alignY: 'above' | 'below';
}

/** The gap the card keeps from the placement it hangs off, matching the prototype's popover. */
const POPOVER_GAP = '16px';

const popoverTranslate = ({ alignX, alignY }: PlacementPopoverCardProps): string =>
  `translate(${popoverTranslateX[alignX]}, ${alignY === 'above' ? `calc(-100% - ${POPOVER_GAP})` : POPOVER_GAP})`;

export const PlacementPopoverCard = styled.div<PlacementPopoverCardProps>`
  position: absolute;
  width: 260px;
  padding: 12px 14px;
  box-sizing: border-box;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.universal.white};
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  box-shadow: 0 8px 24px rgba(23, 30, 38, 0.16);
  /*
   * Scale first, translate second: the plan's own transform multiplies whatever this produces, so a
   * translate placed outside the scale would come out multiplied by the zoom. Nested inside it, the
   * offsets — and the percentages, which resolve against the card's layout size — land as the exact
   * pixels intended on screen. Scaling by the inverse zoom cancels the plan's, so the card keeps its
   * size at every zoom level.
   */
  transform-origin: 0 0;
  transform: scale(${({ scale }) => 1 / scale}) ${popoverTranslate};
`;

/* ---- Find a coworker (sheet) ---- */

export const CoworkerSearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 14px;

  svg {
    position: absolute;
    left: 12px;
    color: ${({ theme }) => theme.colors.palette.systemGreyDark};
    pointer-events: none;
  }
`;

export const CoworkerSearchInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 14px 0 36px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  background: ${({ theme }) => theme.colors.universal.white};
  font: 400 14px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
  outline: none;

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const CoworkerList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const CoworkerRow = styled.li`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const CoworkerAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme }) => transparentize(0.9, theme.colors.primary)};
  color: ${({ theme }) => theme.colors.primary};
  font: 700 12.5px/1 ${({ theme }) => theme.fonts.join()};
`;

export const CoworkerRowText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CoworkerRowName = styled.span`
  font: 600 14px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CoworkerRowSubtitle = styled.span`
  font: 400 12.5px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

/** The row's "View": a text button in the brand accent, the prototype's inline link. */
export const CoworkerViewButton = styled.button`
  flex-shrink: 0;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font: 600 13px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    background: ${({ theme }) => transparentize(0.92, theme.colors.primary)};
  }
`;

/** Under the rows when the directory is longer than what is loaded: fetches the next page. */
export const CoworkerShowMoreButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font: 600 13px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => transparentize(0.92, theme.colors.primary)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/**
 * "N of M booked today" — the prototype's grey pill above the rows, the count bold so the eye lands
 * on it first. Sits above the list (not under it): it summarises what the search matched
 * before the rows enumerate it.
 */
export const CoworkerSummary = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.palette.systemGreyLight};
  font: 400 12.5px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

/* The summary's line, taking the width so View / Edit sit at the right edge (prototype `flex:1`). */
export const CoworkerSummaryText = styled.span`
  flex: 1;
  min-width: 0;
`;

export const CoworkerSummaryCount = styled.strong`
  font-weight: 700;
`;

/**
 * The count when someone is booked: a toggle that lifts the booked rows to the top, in the building's
 * primary colour so it reads as actionable (the plain grey count above has nothing to sort).
 */
export const CoworkerSummaryCountButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: 700 12.5px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.primary};

  &:hover,
  &[aria-pressed='true'] {
    text-decoration: underline;
  }
`;

export const CoworkerEmptyState = styled.div`
  padding: 40px 12px;
  text-align: center;

  strong {
    display: block;
    margin-bottom: 4px;
    font: 600 14px/20px ${({ theme }) => theme.fonts.join()};
    color: ${({ theme }) => theme.colors.palette.systemBlack};
  }

  span {
    font: 400 13px/18px ${({ theme }) => theme.fonts.join()};
    color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  }
`;

/**
 * The blocked state (§C4) when the building does not reveal names: the prototype's centred title
 * ("Coworker bookings are not shown here") over the reason, in place of the search.
 */
export const CoworkerBlockedNotice = styled.div`
  padding: 30px 20px;
  text-align: center;

  strong {
    display: block;
    margin-bottom: 6px;
    font: 600 14px/20px ${({ theme }) => theme.fonts.join()};
    color: ${({ theme }) => theme.colors.palette.systemBlack};
  }

  span {
    font: 400 13px/20px ${({ theme }) => theme.fonts.join()};
    color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  }
`;

/* ---- Coworker groups (sheet chips, builder, focus-mode banner) ---- */

/* The group chips under the search field: a horizontal scroller like the prototype's, bar hidden. */
export const CoworkerChipsRow = styled.div`
  display: flex;
  gap: 7px;
  overflow-x: auto;
  margin-bottom: 12px;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* A saved group's chip holds two controls (select, delete), so the pill is a container, not a button. */
export const CoworkerGroupChip = styled.div<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 30px;
  border-radius: 9999px;
  border: 1px solid
    ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.palette.systemGreyMedium)};
  background: ${({ isActive, theme }) => (isActive ? theme.colors.primary : theme.colors.universal.white)};
  color: ${({ isActive, theme }) => (isActive ? theme.colors.universal.white : theme.colors.palette.systemBlack)};
  font: 600 12.5px/16px ${({ theme }) => theme.fonts.join()};
  white-space: nowrap;
`;

export const CoworkerGroupChipButton = styled.button`
  height: 100%;
  padding: 0 12px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;

  /* A deletable chip: the × sits where the right padding would be. */
  &:not(:only-child) {
    padding-right: 4px;
  }

  &:disabled {
    cursor: default;
  }
`;

/* "+ New group": the prototype's dashed brand outline. */
export const CoworkerNewGroupChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 30px;
  padding: 0 12px;
  border-radius: 9999px;
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.universal.white};
  color: ${({ theme }) => theme.colors.primary};
  font: 600 12.5px/16px ${({ theme }) => theme.fonts.join()};
  white-space: nowrap;
  cursor: pointer;
`;

/* ---- Delete-group confirmation (RES-1232): a small centred dialog above the sheet's own overlay. ---- */

export const CoworkerConfirmOverlay = styled(FiltersOverlay)`
  z-index: 30;
`;

export const CoworkerConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  transform: translate(-50%, -50%);
  width: min(320px, calc(100vw - 48px));
  padding: 20px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 12px 40px ${({ theme }) => transparentize(0.75, theme.colors.palette.systemBlack)};
`;

export const CoworkerConfirmTitle = styled.h2`
  margin: 0 0 8px;
  font: 700 17px/22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
`;

export const CoworkerConfirmText = styled.p`
  margin: 0 0 18px;
  font: 400 14px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CoworkerConfirmActions = styled.div`
  display: flex;
  gap: 10px;
`;

export const CoworkerConfirmButton = styled.button<{ isDestructive?: boolean }>`
  flex: 1;
  height: 44px;
  border-radius: 10px;
  border: 1px solid
    ${({ isDestructive, theme }) =>
      isDestructive ? theme.colors.palette.systemPink : theme.colors.palette.systemGreyMedium};
  background: ${({ isDestructive, theme }) =>
    isDestructive ? theme.colors.palette.systemPink : theme.colors.universal.white};
  color: ${({ isDestructive, theme }) =>
    isDestructive ? theme.colors.universal.white : theme.colors.palette.systemBlack};
  cursor: pointer;
  font: 600 14px/18px ${({ theme }) => theme.fonts.join()};
`;

/* The summary's right-hand actions ("View" and the "…" menu) when a group is active. */
export const CoworkerSummaryActions = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

export const CoworkerGroupMenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => transparentize(0.92, theme.colors.palette.systemBlack)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/* The popover under the "…": right-aligned to the trigger, floating over the list. */
export const CoworkerGroupMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 3;
  min-width: 176px;
  padding: 4px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 6px 24px ${({ theme }) => transparentize(0.82, theme.colors.palette.systemBlack)};
`;

export const CoworkerGroupMenuItem = styled.button<{ isDestructive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: 500 14px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ isDestructive, theme }) =>
    isDestructive ? theme.colors.palette.systemPink : theme.colors.palette.systemBlack};

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.palette.systemGreyLight};
  }
`;

export const CoworkerGroupMenuDivider = styled.hr`
  margin: 2px 8px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
`;

export const CoworkerFormError = styled.p`
  margin: 8px 0 0;
  font: 400 12.5px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemPink};
`;

/* ---- Group builder ---- */

export const CoworkerBuilderHint = styled.p`
  margin: 0 0 14px;
  font: 400 13px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CoworkerBuilderNameInput = styled.input`
  width: 100%;
  height: 42px;
  margin-bottom: 14px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  background: ${({ theme }) => theme.colors.universal.white};
  box-sizing: border-box;
  font: 400 14px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemBlack};
  outline: none;

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const CoworkerPickList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* One pickable coworker: the whole row toggles, so the row IS the checkbox. */
export const CoworkerPickRow = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 2px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const CoworkerCheckbox = styled.span<{ isChecked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border-radius: 5px;
  box-sizing: border-box;
  border: ${({ isChecked, theme }) => (isChecked ? 'none' : `1.5px solid ${theme.colors.palette.systemGreyMedium}`)};
  background: ${({ isChecked, theme }) => (isChecked ? theme.colors.primary : theme.colors.universal.white)};
  color: ${({ theme }) => theme.colors.universal.white};
  font: 700 12px/1 ${({ theme }) => theme.fonts.join()};
`;

export const CoworkerBuilderFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

/*
 * The find-a-coworker shells: the filters' shells at a FIXED height, so switching between Find,
 * New group and Edit group does not resize the surface — the body scrolls, the header and the
 * builder's footer stay. Mobile takes the prototype's `top:64px`; the web keeps the panel's cap.
 */
/* No grab bar: this sheet is not swipe-dismissible, so a handle would promise a gesture it lacks.
   The extra top padding stands in for the space the handle used to take. */
export const CoworkerSheet = styled(FiltersSheet)`
  height: calc(100% - 64px);
  padding-top: 22px;
  max-height: none;
`;

export const CoworkerPanel = styled(FiltersPanel)`
  height: min(600px, calc(100% - 48px));
`;

export const CoworkerSheetFooter = styled.div`
  flex-shrink: 0;
  padding-top: 12px;
`;

export const CoworkerPanelFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 20px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const CoworkerBuilderMeta = styled.p`
  margin: 0;
  font: 400 12.5px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CoworkerBuilderCancel = styled.button`
  height: 36px;
  border: 0;
  background: transparent;
  font: 600 13px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  cursor: pointer;
`;

/* ---- Focus-mode banner over the map ---- */

/* "Floorplan filtered to <group> — N of M booked today", pinned over the top of the plan (CV-TEAM-05). */
export const CoworkerGroupBannerBar = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.universal.white};
  box-shadow: 0 6px 18px -8px rgba(11, 14, 18, 0.35);
  font: 400 12.5px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CoworkerGroupBannerText = styled.span`
  flex: 1;
  min-width: 0;

  strong {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.palette.systemBlack};
  }
`;

export const CoworkerGroupBannerClear = styled.button`
  flex-shrink: 0;
  padding: 4px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: 600 12.5px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => transparentize(0.92, theme.colors.primary)};
  }
`;
